/**
 * Generics free interface for a Command
 */
export interface Command {
  readonly result: Promise<any>;
  readonly hasExecuted: boolean;

  execute(): Promise<any>;
}

/**
 * Base class for a command
 */
export abstract class CommandBase implements Command {
  constructor() {
    this._result = new Promise<any>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  private _result: Promise<any>;
  get result(): Promise<any> {
    return this._result;
  }

  private resolve = (value: any | PromiseLike<any>): void => {};
  private reject = (reason?: any): void => {};

  private _syncedCommand: Command;
  public get syncedCommand(): Command {
    return this._syncedCommand;
  }

  /***
   * Syncing this command to another means this command cannot be executed,
   * and the result promise will resolve or reject based on the synced
   * command.
   */
  protected sync(command: Command) {
    if (this._syncedCommand) throw 'An attempt was made to sync a command that is already synced.';

    this._syncedCommand = command;
    command.result.then(value => this.resolve(value)).catch(err => this.reject(err));
  }

  private _hasExecuted = false;
  get hasExecuted(): boolean {
    return this._hasExecuted;
  }

  async execute(): Promise<any> {
    if (this._hasExecuted) throw 'An attempt was made to execute an already executed command.';

    if (this._syncedCommand) throw 'An attempt was made to execute a command that is synced to another.';

    this._hasExecuted = true;
    try {
      const value = await this.doWork();
      this.resolve(value);
    } catch (err) {
      this.reject(err);
    }
    await this.result;
  }

  protected abstract doWork(): Promise<any>;
}

/**
 * Helper command for flush notifications
 */
class FlushNotificationCommand extends CommandBase {
  constructor(public message: string) {
    super();
  }
  protected async doWork(): Promise<any> {
    // No work required
  }
}

/**
 * The default commandStream (can be extended)
 */
export class CommandStream<T extends Command> {
  private commands: Array<Command> = [];
  private _bufferedCommandsQueue: Array<Command> = [];
  private _bufferedCommands: Array<Command> = [];
  get bufferedCommands(): Array<T> {
    // The buffered commands are only accurate if we include any queued
    // commands. It's safe (and necessary) to do this even while processing
    // the buffer. An override of the rebuildBuffer would use this to get
    // access to buffer changes if executing async (such as the rebuild using
    // await on a delayAsync)
    this.moveBufferQueueToBuffer();

    return [...(this._bufferedCommands as Array<T>)];
  }

  private _isPaused = false;
  get isPaused(): boolean {
    return this._isPaused;
  }

  constructor() {}

  /**
   * Add a command to the buffer and processes the buffer
   */
  add(command: T) {
    if (command.hasExecuted) throw 'Unable to buffer already executed command';

    // When the buffer is processing we queue the commands for the buffer. This
    // prevents commands from being lost due to a buffer rebuild.
    if (this.isBufferProcessing) {
      this._bufferedCommandsQueue.push(command);
      this.onCommandAdded();
      return;
    }

    this._bufferedCommands.push(command);
    this.onCommandAdded();
    this.processBuffer();
  }

  protected get commandsCount(): number {
    const commandsCount = this._bufferedCommandsQueue.length + this._bufferedCommands.length + this.commands.length;
    return commandsCount;
  }

  pause() {
    this._isPaused = true;
  }

  resume() {
    this._isPaused = false;
    this.processCommands();
  }

  private isBufferProcessing = false;
  private async processBuffer() {
    if (this.isBufferProcessing) return;

    this.isBufferProcessing = true;
    this.moveBufferQueueToBuffer();

    // Rebuild the buffer as needed
    this._bufferedCommands = await this.rebuildBuffer();

    // Take the top command from the buffer and add it to the list of commands for processing
    const hasBufferedCommands = this._bufferedCommands.length > 0;
    if (hasBufferedCommands) {
      // Remove the first element from the buffer and move it to the commands
      const nextCommand = this._bufferedCommands.shift() as T;
      this.addCommand(nextCommand);
    }

    this.moveBufferQueueToBuffer();
    this.isBufferProcessing = false;

    const shouldProcessBuffer = this._bufferedCommands.length > 0;
    if (shouldProcessBuffer) this.processBuffer();
  }

  private moveBufferQueueToBuffer() {
    // Move the commands from bufferedCommandsQueue (commands added during buffer
    // processing) to the buffer and clear the bufferedCommandsQueue.
    this._bufferedCommands.push(...this._bufferedCommandsQueue);
    this._bufferedCommandsQueue = [];
  }

  // Intended to be overridden in derived commandStream
  protected async rebuildBuffer() {
    return this._bufferedCommands;
  }

  protected onCommandAdded() {
    // Intended to be overridden in derived commandStream
  }

  // Adds a command and processes
  private addCommand(command: Command) {
    this.commands.push(command);
    this.processCommands();
  }

  private commandSuccessCallbacks: Array<(command: Command) => void> = [];
  onCommandSuccess(callback: (command: Command) => void): () => void {
    this.commandSuccessCallbacks.push(callback);
    const callbackDetach = () => {
      const callbackIndex = this.commandSuccessCallbacks.indexOf(callback);
      if (callbackIndex == -1) return;
      this.commandSuccessCallbacks.splice(callbackIndex, 1);
    };
    return callbackDetach;
  }

  private raiseCommandSuccess(command: Command) {
    this.commandSuccessCallbacks.forEach(c => c(command));
  }

  private isProcessing = false;
  private async processCommands() {
    if (this.isPaused) return;

    if (this.isProcessing) return;
    this.isProcessing = true;

    let hasCommands = this.commands.length > 0;
    if (hasCommands) {
      const nextCommand = this.commands.shift();

      try {
        await this.executeCommand(nextCommand);
        this.raiseCommandSuccess(nextCommand);
      } catch (error) {
        console.error(error);
        this.pause();
      }
    }

    this.isProcessing = false;
    hasCommands = this.commands.length > 0;
    if (hasCommands) this.processCommands();
  }

  protected async executeCommand(command: Command) {
    await command.execute();
  }

  /**
   * Buffer a command that does nothing, but can be used to notify
   * when the command stream has been executed up to that point.
   * Note: New commands can still be added to the buffer while
   * flushing. The expectation is not to block the stream from
   * adding new commands, but to guarantee that when the flush
   * promise resolves, all buffered commands (at the time the
   * flush was requested) have completed.
   */
  flush(message = ''): Promise<void> {
    const flushNotificationCommand = new FlushNotificationCommand(message);
    this.add(flushNotificationCommand as unknown as T); // Hacky type conversion
    return flushNotificationCommand.result;
  }
}
